import { Box, IconButton } from '@chakra-ui/react';
import { IngestionDocumentValidationErrorReviewFragment } from '@client/graphql/__generated__/types';
import { X } from 'lucide-react';
import { FC, useState } from 'react';
import { HideIssueModal, HideIssueType } from './components/HideIssueModal';

interface DocumentValidationHideProps {
  ingestionDocumentValidationId: string;
  ruleUuid: string;
  contractDocumentUuid: string;
  allHiddenReviews: IngestionDocumentValidationErrorReviewFragment[];
  onHideDocumentValidation?: (
    isHidden: boolean,
    message?: string,
    ingestionDocumentValidationId?: string,
    ruleUuid?: string,
    contractDocumentUuid?: string
  ) => void | Promise<void>;
  isHovered: boolean;
}

export const DocumentValidationHide: FC<DocumentValidationHideProps> = ({
  ingestionDocumentValidationId,
  ruleUuid,
  contractDocumentUuid,
  allHiddenReviews,
  onHideDocumentValidation,
  isHovered,
}) => {
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);

  // Find if this specific rule/contract document combination is hidden
  const existingReview = allHiddenReviews.find(
    (review) =>
      review.ruleUuid === ruleUuid &&
      review.contractDocumentUuid === contractDocumentUuid &&
      review.isHidden
  );

  const isHidden = Boolean(existingReview);

  const handleOpenHideModal = () => {
    setIsHideModalOpen(true);
  };

  const handleCloseHideModal = () => {
    setIsHideModalOpen(false);
  };

  const handleHideIssue = async (message: string) => {
    if (onHideDocumentValidation) {
      await onHideDocumentValidation(
        true,
        message,
        ingestionDocumentValidationId,
        ruleUuid,
        contractDocumentUuid
      );
    }
    setIsHideModalOpen(false);
  };

  const handleUnhideIssue = async () => {
    if (onHideDocumentValidation) {
      await onHideDocumentValidation(
        false,
        undefined,
        ingestionDocumentValidationId,
        ruleUuid,
        contractDocumentUuid
      );
    }
  };

  if (!onHideDocumentValidation) {
    return null;
  }

  return (
    <>
      <Box visibility={isHovered || isHidden ? 'visible' : 'hidden'}>
        <IconButton
          aria-label={isHidden ? 'Unhide validation' : 'Hide validation'}
          color="compliance.text.muted"
          icon={<X size={16} />}
          size="xs"
          variant="link"
          onClick={() => {
            if (isHidden) {
              void handleUnhideIssue();
            } else {
              handleOpenHideModal();
            }
          }}
        />
      </Box>

      <HideIssueModal
        isOpen={isHideModalOpen}
        issueType={HideIssueType.DOCUMENT}
        onClose={handleCloseHideModal}
        onSubmit={handleHideIssue}
      />
    </>
  );
};
